import { createSignal, onMount, createEffect, JSX } from "solid-js";
import { config } from "~/data/config";
import { goBack, openModalV2 } from "~/shared_states/modal";
import { ButtonRegular } from "~/widgets/button";

export type CardDetails = {
  name: string;
  number: string;
  cvv: string;
  expiry_month: string;
  expiry_year: string;
};

export type ValidatorResult = {
  isValid: boolean;
  errorMessage?: string;
};

const cardValidator = (cardNumber: string): ValidatorResult => {
  const cleanNumber = cardNumber.replace(/\s/g, "");

  if (cleanNumber.length === 0) {
    return { isValid: true };
  }

  if (cleanNumber.length < 13) {
    return {
      isValid: false,
      errorMessage: "Card number must be at least 13 digits",
    };
  }

  if (cleanNumber.length > 19) {
    return {
      isValid: false,
      errorMessage: "Card number cannot exceed 19 digits",
    };
  }

  if (!/^[0-9]+$/.test(cleanNumber)) {
    return {
      isValid: false,
      errorMessage: "Card number must contain only digits",
    };
  }

  // Luhn algorithm validation
  let sum = 0;
  let shouldDouble = false;

  for (let i = cleanNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanNumber.charAt(i));

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  const isValidLuhn = sum % 10 === 0;
  if (!isValidLuhn) {
    return { isValid: false, errorMessage: "Invalid card number" };
  }

  return { isValid: true };
};

const cvvValidator = (cvv: string): ValidatorResult => {
  if (cvv.length === 0) {
    return { isValid: true };
  }

  if (cvv.length < 3) {
    return {
      isValid: false,
      errorMessage: "CVV must be at least 3 digits",
    };
  }

  if (cvv.length > 4) {
    return {
      isValid: false,
      errorMessage: "CVV cannot exceed 4 digits",
    };
  }

  if (!/^[0-9]+$/.test(cvv)) {
    return {
      isValid: false,
      errorMessage: "CVV must contain only digits",
    };
  }

  return { isValid: true };
};

const monthValidator = (month: string): ValidatorResult => {
  if (month.length === 0) {
    return { isValid: true };
  }

  if (month.length !== 2) {
    return {
      isValid: false,
      errorMessage: "Month must be 2 digits",
    };
  }

  const monthNum = parseInt(month);
  if (monthNum < 1 || monthNum > 12) {
    return {
      isValid: false,
      errorMessage: "Invalid month (01-12)",
    };
  }

  return { isValid: true };
};

const yearValidator = (year: string, month: string): ValidatorResult => {
  if (year.length === 0) {
    return { isValid: true };
  }

  if (year.length < 2) {
    return {
      isValid: false,
      errorMessage: "Year must be at least 2 digits",
    };
  }

  if (year.length > 4) {
    return {
      isValid: false,
      errorMessage: "Year must be at most 4 digits",
    };
  }

  if (year.length === 3) {
    return {
      isValid: false,
      errorMessage: "Year must be 2 or 4 digits",
    };
  }

  // Convert 4-digit year to 2-digit for validation
  let yearNum: number;
  if (year.length === 4) {
    const fullYear = parseInt(year);
    yearNum = fullYear % 100;
  } else {
    yearNum = parseInt(year);
  }

  const monthNum = parseInt(month);
  const currentYear = new Date().getFullYear() % 100;
  const currentMonth = new Date().getMonth() + 1;

  if (yearNum < currentYear) {
    return {
      isValid: false,
      errorMessage: "Card has expired",
    };
  }

  if (
    yearNum === currentYear &&
    month.length === 2 &&
    monthNum < currentMonth
  ) {
    return {
      isValid: false,
      errorMessage: "Card has expired",
    };
  }

  return { isValid: true };
};

type FormInputProps = {
  type?: string;
  value: string;
  onInput: (e: InputEvent & { currentTarget: HTMLInputElement }) => void;
  placeholder?: string;
  hasError?: boolean;
  class?: string;
  maxlength?: number;
};

const FormInput = (props: FormInputProps) => {
  return (
    <input
      type={props.type || "text"}
      value={props.value}
      onInput={props.onInput}
      placeholder={props.placeholder}
      maxlength={props.maxlength}
      class={`autofill-text-fix w-full rounded-lg border bg-baseTertiaryLight px-3 py-2 outline-none placeholder:text-normal placeholder:text-textNormal disabled:bg-baseTertiaryMedium dark:border-basePrimaryDark ${
        props.hasError ? "border-errorDark " : "border-baseTertiaryDark  "
      } ${props.class || ""}`}
    />
  );
};

type FormFieldProps = {
  label: string;
  error?: string;
  children: JSX.Element;
  class?: string;
};

const FormField = (props: FormFieldProps) => {
  return (
    <div class={props.class}>
      <label class="text-sm mb-1 line-clamp-1 block overflow-clip text-[13px] font-medium text-basePrimaryDark">
        {props.label}
      </label>
      {props.children}
      {
        <p
          class="mt-1 text-small text-red-600 transition-opacity duration-300"
          classList={{
            "opacity-0 scale-y-0": !props.error,
          }}
        >
          {props.error ?? " "}
        </p>
      }
    </div>
  );
};

const useCardDetailsForm = (props: {
  cardNumberLiveValidator?: (cardNumber: string) => ValidatorResult;
}) => {
  const [cardName, setCardName] = createSignal("");
  const [cardNumber, setCardNumber] = createSignal("");
  const [cvv, setCvv] = createSignal("");
  const [expiryMonth, setExpiryMonth] = createSignal("");
  const [expiryYear, setExpiryYear] = createSignal("");
  const [cardError, setCardError] = createSignal<string>();
  const [cvvError, setCvvError] = createSignal<string>();
  const [monthError, setMonthError] = createSignal<string>();
  const [yearError, setYearError] = createSignal<string>();
  const [submitAttempted, setSubmitAttempted] = createSignal(false);

  // onMount(() => {
  //   if (config.env === "Prod") {
  //     return;
  //   }
  //   setCardName("John Doe");
  //   setCardNumber("2305324257848228");
  //   setCvv("123");
  //   setExpiryMonth("01");
  //   setExpiryYear("28");
  // });

  createEffect(() => {
    if (props.cardNumberLiveValidator) {
      const result = props.cardNumberLiveValidator(cardNumber());
      if (!result.isValid) {
        setCardError(result.errorMessage);
        return;
      }
    }

    if (submitAttempted()) {
      const result = cardValidator(cardNumber());
      if (!result.isValid) {
        setCardError(result.errorMessage);
        return;
      }
    }

    setCardError(undefined);
  });

  createEffect(() => {
    if (submitAttempted()) {
      const result = cvvValidator(cvv());
      if (!result.isValid) {
        setCvvError(result.errorMessage);
        return;
      }
    }
    setCvvError(undefined);
  });

  createEffect(() => {
    if (submitAttempted()) {
      const result = monthValidator(expiryMonth());
      if (!result.isValid) {
        setMonthError(result.errorMessage);
        return;
      }
    }
    setMonthError(undefined);
  });

  createEffect(() => {
    if (submitAttempted()) {
      const result = yearValidator(expiryYear(), expiryMonth());
      if (!result.isValid) {
        setYearError(result.errorMessage);
        return;
      }
    }
    setYearError(undefined);
  });

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  const handleCardNumberInput = (value: string) => {
    const formatted = formatCardNumber(value);
    if (formatted.replace(/\s/g, "").length <= 16) {
      setCardNumber(formatted);
    }
  };

  const handleCvvInput = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    setCvv(numericValue);
  };

  const handleMonthInput = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    setExpiryMonth(numericValue);
  };

  const handleYearInput = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    setExpiryYear(numericValue);
  };

  const isFormValid = () => {
    if (props.cardNumberLiveValidator) {
      const validationResult = props.cardNumberLiveValidator(cardNumber());
      if (!validationResult.isValid) {
        setCardError(validationResult.errorMessage);
        return false;
      }
    }
    const cardValidation = cardValidator(cardNumber());

    return (
      cardName().trim().length > 0 &&
      cardNumber().replace(/\s/g, "").length >= 13 &&
      cardValidation.isValid &&
      cvv().length >= 3 &&
      expiryMonth().length === 2 &&
      parseInt(expiryMonth()) >= 1 &&
      parseInt(expiryMonth()) <= 12 &&
      (expiryYear().length === 2 || expiryYear().length === 4) &&
      parseInt(
        expiryYear().length === 4 ? expiryYear().slice(-2) : expiryYear()
      ) >=
        new Date().getFullYear() % 100
    );
  };

  const handleSubmit = (onSubmit: (cardDetails: CardDetails) => void) => {
    setSubmitAttempted(true);

    if (!isFormValid()) return false;

    const cardDetails: CardDetails = {
      name: cardName().trim(),
      number: cardNumber().replace(/\s/g, ""),
      cvv: cvv(),
      expiry_month: expiryMonth(),
      expiry_year:
        expiryYear().length === 4 ? expiryYear().slice(-2) : expiryYear(),
    };

    onSubmit(cardDetails);
    return true;
  };

  return {
    cardName: cardName,
    cardNumber: cardNumber,
    cvv: cvv,
    expiryMonth: expiryMonth,
    expiryYear: expiryYear,
    cardError: cardError,
    cvvError: cvvError,
    monthError: monthError,
    yearError: yearError,
    handleCardNumberInput,
    handleCvvInput,
    handleMonthInput,
    handleYearInput,
    handleSubmit,
    setCardName,
  };
};

export type CardDetailsFormProps = {
  onSubmit: (cardDetails: CardDetails) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  cardNumberLiveValidator?: (cardNumber: string) => ValidatorResult;
  title?: string;
};

export function collectCardDetails(props: {
  onSubmit: (cardDetails: CardDetails) => void;
  cardNumberLiveValidator?: (cardNumber: string) => ValidatorResult;
  title?: string;
}) {
  const [isLoading, setIsLoading] = createSignal(false);

  const handleSubmit = (cardDetails: CardDetails) => {
    setIsLoading(true);
    props.onSubmit(cardDetails);
    goBack();
  };

  openModalV2(
    () => (
      <CardDetailsForm
        onSubmit={handleSubmit}
        onCancel={goBack}
        isLoading={isLoading()}
        cardNumberLiveValidator={props.cardNumberLiveValidator}
        title={props.title}
      />
    ),
    {
      preventClose: false,
    }
  );
}

export function CardDetailsForm(props: CardDetailsFormProps) {
  const form = useCardDetailsForm({
    cardNumberLiveValidator: props.cardNumberLiveValidator,
  });

  const handleSubmit = () => {
    if (props.isLoading) return;
    form.handleSubmit(props.onSubmit);
  };

  return (
    <div class=" w-full ">
      <div class=" w-full  rounded-2xl bg-baseTertiaryLight p-6">
        <h2 class="text-xl mb-6  text-start font-semibold text-basePrimaryDark">
          {props.title || "Enter Card Details"}
        </h2>

        <div class="space-y-4 text-basePrimaryDark">
          <FormField label="Cardholder Name">
            <FormInput
              value={form.cardName()}
              onInput={(e) => form.setCardName(e.currentTarget.value)}
              placeholder="Enter name on card"
            />
          </FormField>

          <FormField label="Card Number" error={form.cardError()}>
            <FormInput
              value={form.cardNumber()}
              onInput={(e) => form.handleCardNumberInput(e.currentTarget.value)}
              placeholder="1234 5678 9012 3456"
              hasError={!!form.cardError()}
            />
          </FormField>

          <div class="flex flex-row gap-4">
            <FormField
              label="Expiry Month"
              class="flex-1"
              error={form.monthError()}
            >
              <FormInput
                value={form.expiryMonth()}
                onInput={(e) => form.handleMonthInput(e.currentTarget.value)}
                placeholder="MM"
                hasError={!!form.monthError()}
                maxlength={2}
              />
            </FormField>

            <FormField
              label="Expiry Year"
              class="flex-1"
              error={form.yearError()}
            >
              <FormInput
                value={form.expiryYear()}
                onInput={(e) => form.handleYearInput(e.currentTarget.value)}
                placeholder="YY"
                hasError={!!form.yearError()}
                maxlength={4}
              />
            </FormField>

            <FormField label="CVV" class="flex-1" error={form.cvvError()}>
              <FormInput
                type="password"
                value={form.cvv()}
                onInput={(e) => form.handleCvvInput(e.currentTarget.value)}
                placeholder="123"
                hasError={!!form.cvvError()}
                maxlength={3}
              />
            </FormField>
          </div>
        </div>

        <div class="mt-6 flex w-full justify-center gap-3">
          <ButtonRegular
            class="w-full"
            onClick={props.onCancel}
            isEnabled={!props.isLoading}
            isOutlined
          >
            Cancel
          </ButtonRegular>
          <ButtonRegular
            class="w-full"
            onClick={handleSubmit}
            isLoading={props.isLoading}
            isEnabled={!props.isLoading}
          >
            Continue
          </ButtonRegular>
        </div>
      </div>
    </div>
  );
}
